import * as React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ExpandMoreIcon from '@assets/images/FilterDownArrow.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import type {
  IndeterminateCheckboxProps,
  Hospital,
  HealthSystem,
} from '../../API/services/consultsService';
import './component.css';
import { useEffect, useState } from 'react';

export default function IndeterminateCheckbox({
  searchText,
  applyButtonCliked,
  healthSystems,
  clearFilter,
  getFacilityCount,
  storedPreviousValues,
}: Readonly<{
  searchText: string;
  applyButtonCliked: (data: IndeterminateCheckboxProps[]) => void;
  healthSystems: HealthSystem[];
  clearFilter: boolean;
  getFacilityCount: (data: IndeterminateCheckboxProps[]) => void;
  storedPreviousValues: IndeterminateCheckboxProps[];
}>) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [filterData, setFilterData] = useState<HealthSystem[]>([]);
  useEffect(() => {
    if (healthSystems) {
      setFilterData(structuredClone(healthSystems));
    }
  }, [healthSystems]);

  const [checkedIds, setCheckedIds] = useState<IndeterminateCheckboxProps[]>(
    structuredClone(storedPreviousValues)
  );
  useEffect(() => {
    if (clearFilter) {
      setCheckedIds([]);
    }
  }, [clearFilter]);

  useEffect(() => {
    const dataForSearch = structuredClone(healthSystems);
    const searchedObject = dataForSearch?.filter(
      (facility) =>
        facility?.facilities?.some((hospital) =>
          hospital.name.toLowerCase().includes(searchText.toLowerCase())
        ) || facility.name.toLowerCase().includes(searchText.toLowerCase())
    );

    setFilterData(searchedObject);
  }, [searchText, healthSystems]);

  console.log('filterData:', filterData);
  console.log('length:', filterData?.length);

  const handleChangeFacility = (
    e: React.ChangeEvent<HTMLInputElement>,
    faciltyId: number,
    faciltyName: string,
    hospitalIds: Hospital[]
  ) => {
    const prevCheckedIds = [...checkedIds];
    let newCheckedIds: IndeterminateCheckboxProps[] = [];

    const existingFacility: IndeterminateCheckboxProps | undefined =
      prevCheckedIds?.find(
        (item: IndeterminateCheckboxProps) => item.facility === faciltyId
      );

    if (existingFacility) {
      existingFacility.checked = e.target.checked;
      existingFacility.hospitals.forEach((hospital) => {
        hospital.checked = e.target.checked;
      });
      existingFacility.checkFacility = e.target.checked;

      if (
        existingFacility.checkInderterminate === true &&
        e.target.checked === true
      ) {
        existingFacility.checkFacility = null;
        existingFacility.checkInderterminate = false;
        existingFacility.hospitals.forEach((hospital) => {
          hospital.checked = !e.target.checked;
        });
      }

      newCheckedIds = [...prevCheckedIds];
    } else {
      const newFacility = {
        faciltyName: faciltyName,
        facility: faciltyId,
        checked: e.target.checked,
        hospitals: hospitalIds.map((hospitalItem) => ({
          id: hospitalItem.id,
          name: hospitalItem.name,
          checked: e.target.checked,
        })),
        checkFacility: e.target.checked,
        checkInderterminate: !e.target.checked,
      };

      newCheckedIds = prevCheckedIds.concat([newFacility]);
    }

    setCheckedIds(() => newCheckedIds);
    getFacilityCount(newCheckedIds);
  };
  const handleChangeHospital = (
    e: React.ChangeEvent<HTMLInputElement>,
    facilityId: number,
    hospitalId: number,
    hospitalList: Hospital[],
    faciltyName: string
  ) => {
    const prevCheckedIds = [...checkedIds];
    let newCheckedIds: IndeterminateCheckboxProps[] = [];

    const existingFacility = prevCheckedIds?.find(
      (item) => item.facility === facilityId
    );
    if (existingFacility) {
      const hospitalIndex = existingFacility.hospitals.findIndex(
        (hospital) => hospital.id === hospitalId
      );

      if (hospitalIndex !== -1) {
        existingFacility.hospitals[hospitalIndex].checked = e.target.checked;
        const allHospitalsChecked = existingFacility.hospitals.every(
          (hospital) => hospital.checked
        );
        existingFacility.checked = allHospitalsChecked;
      }

      const hasAnyHospitalUnchecked = existingFacility?.hospitals?.some(
        (hospital) => !hospital.checked
      );

      if (hasAnyHospitalUnchecked) {
        existingFacility.checkFacility = false;
        existingFacility.checkInderterminate = true;
      }
      const allHospitalsUnchecked = existingFacility?.hospitals?.every(
        (hospital) => !hospital.checked
      );
      const allHospitalschecked = existingFacility?.hospitals?.every(
        (hospital) => hospital.checked
      );

      if (allHospitalsUnchecked) {
        existingFacility.checkInderterminate = false;
        existingFacility.checkFacility = null;
      } else if (allHospitalschecked) {
        existingFacility.checkInderterminate = false;
        existingFacility.checkFacility = true;
      } else {
        existingFacility.checkInderterminate = true;
        existingFacility.checkFacility = null;
      }
      newCheckedIds = [...prevCheckedIds];
    } else {
      const newFacility = {
        faciltyName: faciltyName,
        facility: facilityId,
        checked: e.target.checked,
        hospitals: hospitalList.map((hospitalItem) => ({
          id: hospitalItem.id,
          name: hospitalItem.name,
          checked: false,
        })),
        checkInderterminate: true,
        checkFacility: false,
      };

      newFacility.hospitals.forEach((hospital) => {
        if (hospital.id === hospitalId) {
          hospital.checked = e.target.checked;
          if (newFacility.hospitals.length === 1) {
            newFacility.checkFacility = e.target.checked;
            newFacility.checkInderterminate = !e.target.checked;
          }
        }
      });
      newCheckedIds = prevCheckedIds.concat([newFacility]);
    }
    setCheckedIds(() => newCheckedIds);
    getFacilityCount(newCheckedIds);
  };

  const children = (
    hospital: Hospital[],
    faciltyId: number,
    faciltyName: string
  ) => {
    return hospital.map((hospitalVal: Hospital) => (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}
        key={hospitalVal.id}
      >
        <FormControlLabel
          label={hospitalVal.name}
          control={
            <Checkbox
              checked={
                checkedIds
                  ?.find((item) => item.facility === faciltyId)
                  ?.hospitals?.find(
                    (hospital) => hospital.id === hospitalVal.id
                  )?.checked === true
              }
              onChange={(e) =>
                handleChangeHospital(
                  e,
                  faciltyId,
                  hospitalVal.id,
                  hospital,
                  faciltyName
                )
              }
            />
          }
        />
      </Box>
    ));
  };
  const handleExpand = (id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const handleApplyButtonClick = () => {
    applyButtonCliked(checkedIds);
  };

  return (
    <Box className="ml-20 mr-20">
      {(!filterData || filterData.length === 0) && (
        <Box className="text-align-center">
          <CustomTypography
            variant="body2"
            color="textDisabled"
            id="noAcceptedConsults"
          >
            No records found
          </CustomTypography>
        </Box>
      )}
      {filterData?.length > 0 &&
        filterData?.map((facilityVal) => (
          <Box key={facilityVal.id}>
            <Box className="flex-justify-space-between" gap={6}>
              <FormControlLabel
                label={facilityVal.name}
                control={
                  <Checkbox
                    checked={Boolean(
                      checkedIds.find(
                        (item) => item.facility === facilityVal.id
                      )?.checkFacility
                    )}
                    indeterminate={
                      checkedIds.find(
                        (item) => item.facility === facilityVal.id
                      )?.checkInderterminate ?? false
                    }
                    onChange={(e) =>
                      handleChangeFacility(
                        e,
                        facilityVal.id,
                        facilityVal.name,
                        facilityVal.facilities
                      )
                    }
                  />
                }
              />

              <Box
                className="filter-expand-icon"
                onClick={() => handleExpand(facilityVal.id)}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    transform: expanded[facilityVal.id]
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <ExpandMoreIcon />
                </Box>
              </Box>
            </Box>

            {expanded?.[facilityVal.id] &&
              children(
                facilityVal.facilities,
                facilityVal.id,
                facilityVal.name
              )}
          </Box>
        ))}
      {filterData?.length !== 0 && (
        <Box className="mt-20">
          <CustomButton
            id="btnFilterApply"
            variant="contained"
            color="primary"
            className="button-width"
            onClick={handleApplyButtonClick}
          >
            Apply
          </CustomButton>
        </Box>
      )}
    </Box>
  );
}
